import type { DatePickerProps, FloatingAlign, FloatingSide, TextInputContainerProps } from '@_/ui';
import type { DateTime } from 'luxon';
import { usePrevious } from '@mantine/hooks';
import clsx from 'clsx';
import { useCallback, useEffect, useRef, useState } from 'react';
import { PiCalendarDots } from 'react-icons/pi';
import { FormattedMessage } from 'react-intl';
import { useDialog, useTailwindBreakpoint } from '@_/hooks';
import { buttonCommonI18n, promptCommonI18n } from '@_/i18n-common';
import {
  Button,
  DatePicker,
  FormattedDateRange,
  Popover,
  PopoverPopup,
  PopoverTrigger,
  Text,
  TextInputContainer
} from '@_/ui';
import { isTruthy } from '@_/utils';
import { DatePickerDialog } from '../DatePickerDialog/index.ts';

export interface DatePickerInputProps
  extends
    Pick<
      DatePickerProps,
      | 'include'
      | 'maxDate'
      | 'minDate'
      | 'numDisplayMonths'
      | 'onChange'
      | 'ranged'
      | 'renderDay'
      | 'timezone'
      | 'value'
    >,
    Pick<TextInputContainerProps, 'disabled' | 'error' | 'label'> {
  disabled?: boolean;
  align?: FloatingAlign;
  placeholder?: string;
  renderDatePicker?: (props: DatePickerProps) => React.ReactNode;
  renderValue?: (dates: DateTime[]) => React.ReactNode;
  rounded?: boolean;
  side?: FloatingSide;
  size?: 'md' | 'lg';
  updateOnClose?: boolean;
}

export function DatePickerInput({
  align = 'center',
  disabled,
  error,
  label,
  onChange,
  placeholder,
  ranged,
  renderDatePicker,
  renderValue,
  rounded = false,
  size = 'md',
  side = 'bottom',
  updateOnClose = false,
  value,
  ...otherProps
}: DatePickerInputProps) {
  const isDesktop = useTailwindBreakpoint('sm');

  const valueRef = useRef(value);
  valueRef.current = value;

  // Keep track of the interim value for date ranges before the datepicker is
  // closed
  const [internalValue, setInternalValue] = useState(value);
  const internalValueRef = useRef(internalValue);
  internalValueRef.current = internalValue;

  const previousInternalValue = usePrevious(internalValue);

  // Keep internal value in sync
  useEffect(() => {
    setInternalValue(value);
    internalValueRef.current = value;
  }, [value]);

  const [open, setOpen] = useState(false);
  const previousOpen = usePrevious(open);

  // Close datepicker after selecting a date or range of dates
  useEffect(() => {
    const valueChanged = internalValue !== previousInternalValue;
    const fulfilledRequirements = !ranged || (ranged && internalValue?.length === 2);

    if (updateOnClose && valueChanged && fulfilledRequirements) {
      setOpen(false);
    }
  }, [internalValue, previousInternalValue, ranged, updateOnClose]);

  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  // When datepicker is closed
  useEffect(() => {
    const wasJustClosed = !open && open !== previousOpen && typeof previousOpen === 'boolean';

    if (wasJustClosed) {
      if (updateOnClose) {
        // Propagate updates
        onChangeRef.current?.(internalValueRef.current || null);
      } else {
        // Reset internal date(s) back to the original value
        setInternalValue(valueRef.current);
      }
    }
  }, [open, previousOpen, updateOnClose]);

  const handleClickSave = useCallback(() => {
    onChangeRef.current?.(internalValueRef.current || null);
    setOpen(false);
  }, [setOpen]);

  const datePickerProps: DatePickerProps = {
    ...otherProps,
    onChange: setInternalValue,
    ranged,
    value: internalValue
  };

  const datePickerDialog = useDialog({
    component: DatePickerDialog,
    props: {
      ...datePickerProps,
      onChange
    }
  });

  let valueDisplay: React.ReactNode;
  if (internalValue?.length) {
    if (renderValue) {
      valueDisplay = renderValue(internalValue);
    } else {
      valueDisplay = internalValue?.some(isTruthy) ? (
        <FormattedDateRange dateStyle="medium" values={internalValue.filter(isTruthy)} />
      ) : undefined;
    }
  }

  let placeholderDisplay: React.ReactNode = placeholder;
  if (!placeholderDisplay) {
    placeholderDisplay = ranged ? (
      <FormattedMessage {...promptCommonI18n.selectDates} />
    ) : (
      <FormattedMessage {...promptCommonI18n.selectDate} />
    );
  }

  const buttonClassName = clsx(
    'flex w-full items-center text-left leading-6',
    size === 'md' && 'px-3 py-[calc(--spacing(1.5)-1px)]',
    size === 'lg' && 'px-4 py-2'
  );

  const buttonContent = (
    <>
      <div className="mr-1.5 flex size-4.5 flex-none place-content-center place-items-center">
        <PiCalendarDots className="size-full text-gray-600" />
      </div>
      <div>
        {valueDisplay ? (
          <Text semibold>{valueDisplay}</Text>
        ) : (
          <span className="font-normal text-gray-400">{placeholderDisplay}</span>
        )}
      </div>
    </>
  );

  return (
    <>
      {datePickerDialog.element}

      <Popover onOpenChange={setOpen} open={isDesktop && open}>
        <div className="w-full min-w-48">
          <TextInputContainer
            disabled={disabled}
            error={error}
            focused={isDesktop && open}
            label={label}
            rounded={rounded ? 'full' : 'normal'}
          >
            {isDesktop ? (
              <PopoverTrigger className={buttonClassName} disabled={disabled} render={<button />}>
                {buttonContent}
              </PopoverTrigger>
            ) : (
              <button
                className={buttonClassName}
                disabled={disabled}
                onClick={() => datePickerDialog.open()}
                type="button"
              >
                {buttonContent}
              </button>
            )}
          </TextInputContainer>
        </div>

        {isDesktop && (
          <PopoverPopup
            align={align}
            className="rounded-lg border border-gray-100 bg-white p-8 py-6 shadow-xl"
            height="natural"
            positionMethod="fixed"
            side={side}
            sideOffset={4}
          >
            {renderDatePicker ? (
              renderDatePicker(datePickerProps)
            ) : (
              <DatePicker {...datePickerProps} />
            )}
            {!updateOnClose && (
              <div className="flex justify-end gap-x-4">
                <Button
                  disabled={!internalValue?.length}
                  onClick={() => setInternalValue(null)}
                  variant="plain"
                >
                  <FormattedMessage {...buttonCommonI18n.clearDates} />
                </Button>
                <Button color="primary" onClick={handleClickSave}>
                  <FormattedMessage {...buttonCommonI18n.save} />
                </Button>
              </div>
            )}
          </PopoverPopup>
        )}
      </Popover>
    </>
  );
}
